import { createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";
import {
  cubicBezier,
  fontFamily,
  layout,
  motion,
  palettes,
  radius,
  space,
  typeScale,
} from "@inkhunt/core";
import type { Palette, TypeRole, TypeToken } from "@inkhunt/core";

// The Inkhunt theme (see apps/web/DESIGN.md). Every value comes from the shared tokens in
// @inkhunt/core, which the mobile app reads too. Light is the default and the OS decides when
// to switch to dark: MUI emits both palettes as CSS variables under a prefers-color-scheme
// media query, and `tokenStyles` below does the same for the --ih-* variables the CSS modules use.

const kebab = (key: string) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
const rem = (px: number) => `${+(px / 16).toFixed(4)}rem`;

// Interpolates a type token from its phone size at 390px to its desktop size at 1440px.
const fluidSize = ({ min, max }: TypeToken) => {
  if (min === max) return rem(min);
  const slope = (max - min) / (1440 - 390);
  const intercept = min - slope * 390;
  return `clamp(${rem(min)}, calc(${rem(intercept)} + ${+(slope * 100).toFixed(4)}vw), ${rem(max)})`;
};

const typeStyle = (role: TypeRole) => {
  const token: TypeToken = typeScale[role];
  return {
    fontSize: fluidSize(token),
    lineHeight: token.lineHeight,
    fontWeight: token.weight,
    letterSpacing: `${token.tracking}em`,
  };
};

const ease = cubicBezier(motion.easeOut);
const ms = (value: number) => `${value}ms`;

const shadowPop = {
  light: "0 12px 40px rgba(28, 27, 26, 0.14), 0 2px 6px rgba(28, 27, 26, 0.06)",
  dark: "0 12px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(244, 242, 240, 0.06)",
};

const paletteVars = (p: Palette) =>
  Object.fromEntries(Object.entries(p).map(([key, value]) => [`--ih-${kebab(key)}`, value]));

const typeVars = Object.fromEntries(
  (Object.keys(typeScale) as TypeRole[]).flatMap((role) => {
    const style = typeStyle(role);
    return [
      [`--ih-${role}-size`, style.fontSize],
      [`--ih-${role}-leading`, String(style.lineHeight)],
      [`--ih-${role}-weight`, String(style.fontWeight)],
      [`--ih-${role}-tracking`, style.letterSpacing],
    ];
  })
);

/** Global CSS variables for the CSS modules. Rendered once through <GlobalStyles>. */
export const tokenStyles = {
  ":root": {
    ...paletteVars(palettes.light),
    ...typeVars,
    ...Object.fromEntries(Object.entries(space).map(([k, v]) => [`--ih-space-${k}`, `${v}px`])),
    ...Object.fromEntries(Object.entries(radius).map(([k, v]) => [`--ih-radius-${k}`, `${v}px`])),
    ...Object.fromEntries(
      Object.entries(motion.duration).map(([k, v]) => [`--ih-dur-${k}`, ms(v)])
    ),
    "--ih-font": fontFamily.webStack,
    "--ih-ease-out": ease,
    "--ih-ease-in-out": cubicBezier(motion.easeInOut),
    "--ih-press-scale": String(motion.pressScale),
    "--ih-nav-h": `${layout.navHeight}px`,
    "--ih-content-max": `${layout.contentMax}px`,
    "--ih-text-measure": `${layout.textMeasure}px`,
    "--ih-form-max": `${layout.formMax}px`,
    "--ih-gutter": "clamp(16px, 4vw, 40px)",
    "--ih-shadow-pop": shadowPop.light,
    "--ih-z-nav": "1100",
    colorScheme: "light",
  },
  "@media (prefers-color-scheme: dark)": {
    ":root": {
      ...paletteVars(palettes.dark),
      "--ih-shadow-pop": shadowPop.dark,
      colorScheme: "dark",
    },
  },
};

const muiPalette = (p: Palette) => ({
  primary: { main: p.action, dark: p.actionHover, light: p.actionHover, contrastText: p.onAction },
  secondary: { main: p.accent, contrastText: p.onAccent },
  error: { main: p.danger, contrastText: p.onAccent },
  success: { main: p.success },
  warning: { main: p.warning },
  info: { main: p.ink },
  background: { default: p.canvas, paper: p.surface },
  text: { primary: p.ink, secondary: p.inkSecondary },
  divider: p.line,
});

// Elevation is reserved for floating layers, so every non-zero level is the one soft shadow.
const shadows = ["none", ...Array(24).fill("var(--ih-shadow-pop)")] as Shadows;

const focusRing = {
  outline: "2px solid var(--ih-focus)",
  outlineOffset: "2px",
};

const appTheme = createTheme({
  cssVariables: { colorSchemeSelector: "media" },
  colorSchemes: {
    light: { palette: muiPalette(palettes.light) },
    dark: { palette: muiPalette(palettes.dark) },
  },
  shape: { borderRadius: radius.md },
  shadows,
  typography: {
    fontFamily: fontFamily.webStack,
    h1: typeStyle("hero"),
    h2: typeStyle("title"),
    h3: typeStyle("headline"),
    h4: typeStyle("tagline"),
    h5: { ...typeStyle("body"), fontWeight: 600 },
    h6: { ...typeStyle("label"), fontWeight: 600 },
    subtitle1: typeStyle("lead"),
    subtitle2: typeStyle("label"),
    body1: typeStyle("body"),
    body2: typeStyle("callout"),
    caption: typeStyle("caption"),
    button: { ...typeStyle("label"), textTransform: "none" },
  },
  transitions: {
    easing: {
      easeInOut: cubicBezier(motion.easeInOut),
      easeOut: ease,
      easeIn: cubicBezier(motion.easeInOut),
      sharp: ease,
    },
  },
  components: {
    MuiButtonBase: {
      // No Material ripple: presses scale instead, and keyboard focus draws the ring below.
      defaultProps: { disableRipple: true },
      styleOverrides: { root: { "&.Mui-focusVisible": focusRing } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          minHeight: 40,
          paddingInline: 20,
          whiteSpace: "nowrap",
          transition: `background-color ${ms(motion.duration.fast)} ${ease}, color ${ms(
            motion.duration.fast
          )} ${ease}, border-color ${ms(motion.duration.fast)} ${ease}, transform ${ms(
            motion.duration.fast
          )} ${ease}`,
          "&:active": { transform: `scale(${motion.pressScale})` },
        },
        sizeSmall: { minHeight: 32, paddingInline: 14, fontSize: "0.875rem" },
        sizeLarge: { minHeight: 48, paddingInline: 26, fontSize: "1.0625rem" },
        contained: {
          "&.Mui-disabled": {
            backgroundColor: "var(--ih-fill-strong)",
            color: "var(--ih-ink-secondary)",
          },
        },
        outlined: {
          borderColor: "var(--ih-line-strong)",
          color: "var(--ih-ink)",
          "&:hover": { backgroundColor: "var(--ih-fill)", borderColor: "var(--ih-ink)" },
          "&.Mui-disabled": {
            borderColor: "var(--ih-line)",
            color: "var(--ih-ink-secondary)",
            opacity: 0.7,
          },
        },
        text: {
          color: "var(--ih-ink)",
          "&:hover": { backgroundColor: "var(--ih-fill)" },
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: {
            backgroundColor: "var(--ih-action)",
            color: "var(--ih-on-action)",
            "&:hover": { backgroundColor: "var(--ih-action-hover)" },
          },
        },
        {
          props: { variant: "contained", color: "error" },
          style: {
            backgroundColor: "var(--ih-danger)",
            color: "var(--ih-on-accent)",
            "&:hover": { backgroundColor: "var(--ih-danger)", filter: "brightness(0.92)" },
          },
        },
        {
          props: { variant: "outlined", color: "error" },
          style: {
            borderColor: "var(--ih-danger)",
            color: "var(--ih-danger)",
            "&:hover": {
              backgroundColor: "var(--ih-danger-soft)",
              borderColor: "var(--ih-danger)",
            },
          },
        },
        {
          props: { variant: "text", color: "error" },
          style: { color: "var(--ih-danger)", "&:hover": { backgroundColor: "var(--ih-danger-soft)" } },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `background-color ${ms(motion.duration.fast)} ${ease}, transform ${ms(
            motion.duration.fast
          )} ${ease}`,
          "&:hover": { backgroundColor: "var(--ih-fill)" },
          "&:active": { transform: `scale(${motion.pressScale})` },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: radius.lg },
        outlined: { borderColor: "var(--ih-line)" },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { border: "1px solid var(--ih-line)", borderRadius: 14 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 8, minWidth: 220 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...typeStyle("callout"),
          borderRadius: radius.sm,
          minHeight: 40,
          "&:hover": { backgroundColor: "var(--ih-fill)" },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: "var(--ih-ink-secondary)" } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "var(--ih-line)" } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: "var(--ih-canvas)",
          transition: `box-shadow ${ms(motion.duration.fast)} ${ease}`,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--ih-line-strong)",
            transition: `border-color ${ms(motion.duration.fast)} ${ease}`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ih-ink)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--ih-ink)",
            borderWidth: 1,
          },
          "&.Mui-focused": { boxShadow: "0 0 0 4px var(--ih-fill-strong)" },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ih-danger)" },
        },
        input: { fontSize: rem(17) },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: "var(--ih-ink-secondary)", "&.Mui-focused": { color: "var(--ih-ink)" } },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          marginTop: 6,
          borderRadius: 14,
          border: "1px solid var(--ih-line)",
          boxShadow: "var(--ih-shadow-pop)",
        },
        listbox: { padding: 6 },
        option: {
          borderRadius: radius.sm,
          minHeight: 40,
          ...typeStyle("callout"),
        },
        groupLabel: {
          ...typeStyle("caption"),
          fontWeight: 500,
          color: "var(--ih-ink-secondary)",
          backgroundColor: "var(--ih-surface)",
          lineHeight: "32px",
        },
        noOptions: { ...typeStyle("callout"), color: "var(--ih-ink-secondary)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          height: 32,
          fontSize: "0.875rem",
          fontWeight: 500,
          transition: `background-color ${ms(motion.duration.fast)} ${ease}`,
        },
        filled: {
          backgroundColor: "var(--ih-fill)",
          color: "var(--ih-ink)",
          "&.MuiChip-clickable:hover": { backgroundColor: "var(--ih-fill-strong)" },
        },
        outlined: { borderColor: "var(--ih-line-strong)" },
        icon: { color: "var(--ih-ink-secondary)", fontSize: 18, marginLeft: 10 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          ...typeStyle("callout"),
          borderRadius: 14,
          alignItems: "center",
          padding: "8px 16px",
          color: "var(--ih-ink)",
        },
      },
      variants: [
        {
          props: { variant: "standard", severity: "warning" },
          style: {
            backgroundColor: "var(--ih-warning-soft)",
            "& .MuiAlert-icon": { color: "var(--ih-warning)" },
          },
        },
        {
          props: { variant: "standard", severity: "info" },
          style: { backgroundColor: "var(--ih-fill)", "& .MuiAlert-icon": { color: "var(--ih-ink)" } },
        },
        {
          props: { variant: "standard", severity: "error" },
          style: {
            backgroundColor: "var(--ih-danger-soft)",
            "& .MuiAlert-icon": { color: "var(--ih-danger)" },
          },
        },
        {
          props: { variant: "standard", severity: "success" },
          style: {
            backgroundColor: "var(--ih-fill)",
            "& .MuiAlert-icon": { color: "var(--ih-success)" },
          },
        },
      ],
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          "&:not(.MuiBackdrop-invisible)": {
            backgroundColor: "rgba(12, 11, 11, 0.42)",
            backdropFilter: "blur(8px)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 22,
          backgroundColor: "var(--ih-surface)",
          overscrollBehavior: "contain",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { ...typeStyle("tagline"), padding: "24px 24px 8px" } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "8px 24px" } },
    },
    MuiDialogContentText: {
      styleOverrides: { root: { ...typeStyle("callout"), color: "var(--ih-ink-secondary)" } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: "16px 24px 24px", gap: 8, "& > :not(style) ~ :not(style)": { marginLeft: 0 } } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...typeStyle("caption"),
          backgroundColor: "var(--ih-ink)",
          color: "var(--ih-canvas)",
          borderRadius: radius.sm,
          padding: "6px 10px",
        },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: "pulse" },
      styleOverrides: {
        root: { backgroundColor: "var(--ih-fill)" },
        rounded: { borderRadius: radius.md },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600 },
        colorDefault: { backgroundColor: "var(--ih-fill-strong)", color: "var(--ih-ink)" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...typeStyle("callout"),
          borderBottom: "1px solid var(--ih-line)",
          padding: "14px 16px",
          fontVariantNumeric: "tabular-nums",
        },
        head: {
          ...typeStyle("caption"),
          fontWeight: 500,
          color: "var(--ih-ink-secondary)",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--ih-surface)",
          color: "var(--ih-ink)",
          boxShadow: "var(--ih-shadow-pop)",
          "&:hover": { backgroundColor: "var(--ih-fill)" },
          "&:active": { boxShadow: "var(--ih-shadow-pop)" },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "var(--ih-ink)",
          textDecorationColor: "var(--ih-line-strong)",
          textUnderlineOffset: "0.2em",
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { colorPrimary: { color: "var(--ih-ink)" } },
    },
  },
});

export default appTheme;
